import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/nav/Navbar";
import { ReactComponent as VendBabeeIcon } from "../assets/vendbabee-icon.svg";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NotFoundPage — 404 catch-all.
 * Shows the VendBabee V+Star icon, the attempted path, an animated
 * countdown ring from 7, and auto-redirects to / when it hits zero.
 * Design: VendBabee v1 dark theme — glass card, gradient accents.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [seconds, setSeconds] = useState(7);
  const logged = useRef(false);

  // Fire-and-forget: log the 404 hit to the backend on mount
  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    axios
      .post(`${API}/analytics/404`, {
        path: location.pathname + location.search,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent || null,
      })
      .catch(() => {}); // silent — analytics must never break the page
  }, [location]);

  useEffect(() => {
    if (seconds <= 0) {
      navigate("/", { replace: true });
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, navigate]);

  // Progress fraction for the SVG ring (0 → 1 over 7 seconds)
  const progress = (7 - seconds) / 7;
  const circumference = 2 * Math.PI * 38; // r = 38
  const offset = circumference * (1 - progress);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div
        style={{
          paddingTop: "var(--header-h)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - var(--header-h))",
          position: "relative",
          overflow: "hidden",
        }}>
        {/* Background glow blob */}
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            height: 400,
            background:
              "radial-gradient(ellipse, rgba(91,140,255,0.08) 0%, rgba(168,85,247,0.04) 40%, transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
            padding: "0 24px",
            maxWidth: 480,
            textAlign: "center",
          }}>
          {/* VendBabee V+Star icon */}
          <div style={{ width: 120, height: 108, opacity: 0.85 }}>
            <VendBabeeIcon style={{ width: "100%", height: "100%" }} />
          </div>

          {/* 404 heading with gradient */}
          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(5rem, 12vw, 8rem)",
              lineHeight: 1,
              letterSpacing: "-0.03em",
              background: "var(--grad-primary)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              margin: 0,
            }}>
            404
          </h1>

          {/* Subheading */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <p
              style={{
                fontFamily: "'Inter', 'Satoshi', sans-serif",
                fontSize: 18,
                fontWeight: 600,
                color: "hsl(var(--foreground))",
                margin: 0,
              }}>
              Page not found
            </p>
            <p
              style={{
                fontFamily: "'Inter', 'Satoshi', sans-serif",
                fontSize: 14,
                color: "hsl(var(--muted-foreground))",
                margin: 0,
                lineHeight: 1.6,
              }}>
              The page{" "}
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  color: "var(--vb-blue)",
                  background: "rgba(91,140,255,0.1)",
                  borderRadius: 6,
                  padding: "2px 8px",
                }}>
                {location.pathname}
              </span>{" "}
              doesn't exist.
            </p>
          </div>

          {/* Countdown ring */}
          <div
            style={{
              position: "relative",
              width: 88,
              height: 88,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              style={{ transform: "rotate(-90deg)", position: "absolute" }}>
              {/* Track */}
              <circle
                cx="44"
                cy="44"
                r="38"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="3"
              />
              {/* Progress arc */}
              <circle
                cx="44"
                cy="44"
                r="38"
                fill="none"
                stroke="url(#countdown-grad)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
              <defs>
                <linearGradient
                  id="countdown-grad"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%">
                  <stop offset="0%" stopColor="#5B8CFF" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
            </svg>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 28,
                fontWeight: 700,
                color: "hsl(var(--foreground))",
                position: "relative",
                zIndex: 1,
              }}>
              {seconds}
            </span>
          </div>

          <p
            style={{
              fontFamily: "'Inter', 'Satoshi', sans-serif",
              fontSize: 13,
              color: "hsl(var(--muted-foreground))",
              margin: 0,
            }}>
            Redirecting to homepage in{" "}
            <span style={{ color: "hsl(var(--foreground))", fontWeight: 600 }}>
              {seconds}s
            </span>
          </p>

          {/* CTA buttons */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
            }}>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="btn-primary"
              style={{
                height: 44,
                padding: "0 24px",
                fontSize: 13,
                borderRadius: 12,
              }}>
              Go Home Now
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn-secondary"
              style={{
                height: 44,
                padding: "0 24px",
                fontSize: 13,
                borderRadius: 12,
              }}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
